import axios from 'axios';
import humps from 'humps';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Bugfender } from '@bugfender/sdk';
import { getToken, removeToken, setToken } from '../helpers/common';
import * as AuthApi from '../api/authApi';

const getAuthHeader = () => ({ Authorization: `Bearer ${getToken()}` });

let refreshTokenPromise = null;

export function setAuthInterceptors(onUnauthenticated) {
  axios.interceptors.request.use(
    (config) => {
      const token = getToken();
      if (token) {
        Object.assign(config.headers, getAuthHeader());
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      // Added new case when request can falls in timeout and need to refresh window
      if (
        (getToken() && error.config && error.response)
          && ([401, 403].includes(error.response.status))
      ) {
        if (!refreshTokenPromise) {
          refreshTokenPromise = AuthApi.refreshTokenRequest().then((data) => {
            const { token } = data;
            if (!token) {
              onUnauthenticated();
              removeToken();
              window.location.reload();
              throw new Error('Unauthorized');
            }
            setToken(token);
            return token;
          }, (err) => {
            onUnauthenticated();
            removeToken();
            return err;
          });
        }

        return refreshTokenPromise.then(
          () => {
            refreshTokenPromise = null;
            Object.assign(error.config.headers, getAuthHeader());
            return axios.request(error.config);
          },
          (err) => {
            window.location.reload();
            Bugfender.sendLog({
              level: 2,
              text: JSON.stringify({
                message: error.response.data.message,
                title: 'Refresh Token Promise Error',
              }),
            });
            Promise.reject(err);
          },
        );
      }
      Bugfender.sendLog({
        level: 2,
        text: JSON.stringify({
          message: error.response.data.message,
          responseURL: error.request.responseURL,
          data: error.config.data || '',
        }),
      });
      return Promise.reject(error);
    },
  );
}

export function setHumpsInterceptors(axiosInstance = axios) {
  axiosInstance.interceptors.request.use((request) => {
    if (request.headers['x-bypass-humps']) {
      return request;
    }

    return {
      ...request,
      data: humps.decamelizeKeys(request.data),
    };
  });

  axiosInstance.interceptors.response.use((response) => ({
    ...response,
    data: humps.camelizeKeys(response.data),
  }));
}

export function setErrorInterceptors(handler) {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const { statusText, status } = error.response;
      handler(`${statusText} (${status})`);
      return Promise.reject(error);
    },
  );
}
